import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { provisionTenant, checkTenantIdAvailable, checkDatabaseNameAvailable, ProvisionRequest } from '../api/tenantApi';
import { getAllClusters, Cluster } from '../api/clusterApi';
import { getEnvironmentDefaults, EnvironmentDefaults } from '../api/environmentConfigApi';
import { initializeAll } from '../api/keyManagementApi';
import AdminRoleConditionEditor from '../components/tenant/AdminRoleConditionEditor';

// ── Environment config ────────────────────────────────────────────────────────

type Env = 'prod' | 'dev' | 'demo';
type ConfigType = 'azure' | 'local';

const ENV_LABELS: Record<Env, string> = { prod: 'Production', dev: 'Development', demo: 'Demo' };
const ENV_COLORS: Record<Env, string> = {
  prod: 'border-green-500 bg-green-600/20 text-green-300',
  dev:  'border-blue-500  bg-blue-600/20  text-blue-300',
  demo: 'border-purple-500 bg-purple-600/20 text-purple-300',
};
const ENV_INACTIVE = 'border-gray-600 bg-gray-700/50 text-gray-400 hover:border-gray-500';

// Used until /environment-config resolves — mirrors the server defaults in
// server/config/index.ts `tenantProvisioning` so the wizard isn't blank on first paint.
const FALLBACK_ENV_DEFAULTS: EnvironmentDefaults = {
  rootDomains: { app: 'ontoris.app', local: 'ontoris.local' },
  namespaces: { prod: 'app-demo', dev: 'app-dev', demo: 'app-demo' },
  jwksLocal: { host: 'identity-provider', port: 8080 },
  secretStores: { nonLocal: 'ontoris-nonprod-akv', localAzure: 'ontoriskeyvault', localConsul: 'secretstore' },
  localInfra: { consulAddr: 'http://consul:8500', vaultAddr: 'https://vault:8200' },
  azureAdIdp: {
    prod: { tenantId: '', clientId: '' },
    dev:  { tenantId: '', clientId: '' },
    demo: { tenantId: '', clientId: '' },
  },
};

function computeUrls(name: string, env: Env, local: boolean, d: EnvironmentDefaults) {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const rootDomain = local ? d.rootDomains.local : d.rootDomains.app;
  const baseUrl = env === 'prod'
    ? `${slug}.${rootDomain}`
    : `${slug}.${env}.${rootDomain}`;
  const keycloakUrl = env === 'prod'
    ? `https://auth.${rootDomain}`
    : `https://auth.${env}.${rootDomain}`;
  const jwksBaseUrl = local
    ? `http://${d.jwksLocal.host}:${d.jwksLocal.port}`
    : `http://identity-provider.${d.namespaces[env]}.svc.cluster.local`;
  return { slug, baseUrl, keycloakUrl, jwksBaseUrl };
}

function pascalCase(slug: string): string {
  return slug.split(/[-_]+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
}

// Default database name pattern: {Local?}{Dev|Demo|""}{TenantName}Datastore
function defaultDatabaseName(tenantName: string, env: Env, local: boolean): string {
  const envLabel = env === 'dev' ? 'Dev' : env === 'demo' ? 'Demo' : '';
  return `${local ? 'Local' : ''}${envLabel}${pascalCase(tenantName)}Datastore`;
}

function defaultSecretStoreName(local: boolean, configType: ConfigType, d: EnvironmentDefaults): string {
  if (!local) return d.secretStores.nonLocal;
  return configType === 'local' ? d.secretStores.localConsul : d.secretStores.localAzure;
}


// ── Step types ────────────────────────────────────────────────────────────────

type Step = 'identity' | 'platform' | 'review';
const STEPS: Step[] = ['identity', 'platform', 'review'];
const STEP_LABELS: Record<Step, string> = {
  identity: '1. Identity',
  platform: '2. Infrastructure',
  review:   '3. Review & Provision',
};

// ── Field helpers ─────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; hint?: string; required?: boolean; children: React.ReactNode }> = ({ label, hint, required, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-300 mb-1">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props}
    className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm disabled:opacity-50 read-only:bg-gray-800 read-only:text-gray-400 read-only:cursor-default"
  />
);

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ children, ...props }) => (
  <select {...props}
    className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 text-gray-100 focus:outline-none focus:border-blue-500 text-sm"
  >
    {children}
  </select>
);

const UrlBadge: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center gap-3 py-2 border-b border-gray-700/50 last:border-0">
    <span className="text-xs text-gray-500 w-28 shrink-0">{label}</span>
    <span className="font-mono text-sm text-blue-300 truncate">{value}</span>
  </div>
);

// ── Result helpers ────────────────────────────────────────────────────────────

// "absent" is not "empty". Three states, rendered differently and never conflated:
//   absent  - the API did not return the field at all (a response-shape mismatch or a
//             deliberately withheld value). Nothing to show and nothing to reveal.
//   empty   - the field came back as '', i.e. the secret genuinely was not configured.
//   present - a real secret: masked, with Show/Hide.
// A missing field must never take this component (and with it the whole success screen) down.
const SecretRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  const [visible, setVisible] = useState(false);
  const state: 'absent' | 'empty' | 'present' =
    value === undefined || value === null ? 'absent' : value.length === 0 ? 'empty' : 'present';
  const secret = value ?? '';

  return (
    <div className="flex items-center justify-between py-1 border-b border-gray-700 text-sm">
      <span className="text-gray-400 w-56 shrink-0">{label}</span>
      {state === 'absent' && (
        <span className="font-mono text-gray-500 italic mr-2"
              title="The provisioning response did not include this field.">
          not returned
        </span>
      )}
      {state === 'empty' && (
        <span className="font-mono text-yellow-400/70 italic mr-2"
              title="The field was returned but is empty - this secret was not configured.">
          not configured
        </span>
      )}
      {state === 'present' && (
        <>
          <span className="font-mono text-green-300 mr-2 break-all">
            {visible ? secret : '•'.repeat(Math.min(secret.length, 32))}
          </span>
          <button onClick={() => setVisible(v => !v)} className="text-xs text-blue-400 hover:text-blue-300 shrink-0">
            {visible ? 'Hide' : 'Show'}
          </button>
        </>
      )}
    </div>
  );
};

interface ResultData {
  // Optional on purpose: these come straight off the provisioning response, which omits a field
  // whenever Keycloak provisioning was skipped or partially failed. SecretRow renders the
  // absent case explicitly.
  appClientSecret?: string;
  externalClientSecret?: string;
  externalOtpClientSecret?: string;
  emailSpiClientId?: string;
  emailSpiSecret?: string;
  redisUsername: string;
  redisPasswordKey: string;
}

// ── Main component ────────────────────────────────────────────────────────────

const emptyForm = (): ProvisionRequest => ({
  tenantId: '', displayName: '', baseUrl: '', protocol: 'https',
  keycloakAdminUrl: '', keycloakPublicUrl: '',
  keycloakAdminUser: 'admin', keycloakAdminPassword: '',
  jwksHost: 'identity-provider', jwksPort: 8080, jwksBaseUrl: '',
  databaseName: '', cluster: '', secretStoreName: '',
  emailNotificationAddress: '', emailNotificationDisplayName: '', acsEndpoint: '',
});

export const TenantProvisionPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep]       = useState<Step>('identity');
  const [env, setEnv]         = useState<Env>('dev');
  const [local, setLocal]     = useState(false);
  const [configType, setConfigType] = useState<ConfigType>('azure');
  const [envDefaults, setEnvDefaults] = useState<EnvironmentDefaults>(FALLBACK_ENV_DEFAULTS);
  const [tenantName, setTenantName] = useState('');          // short slug, e.g. "acme"
  const [form, setForm]       = useState<ProvisionRequest>(emptyForm());
  const [clusters, setClusters] = useState<Cluster[]>([]);
  // A failed /clusters request and a genuinely empty Clusters collection must never look
  // the same. An empty dropdown that actually means "couldn't check" is what makes this
  // wizard appear to have no clusters when the request was simply rejected.
  const [clustersState, setClustersState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [clustersError, setClustersError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [result, setResult]   = useState<ResultData | null>(null);
  const [kcWarning, setKcWarning] = useState<string | null>(null);
  const [keysState, setKeysState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [keysMsg, setKeysMsg] = useState('');
  const [tenantIdCheck, setTenantIdCheck] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const tenantIdDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [databaseNameCheck, setDatabaseNameCheck] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const databaseNameDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  type IdpType = 'none' | 'azure-ad' | 'oidc';
  const [idpType, setIdpType] = useState<IdpType>('none');
  const [idpAlias, setIdpAlias]               = useState('microsoft');
  const [idpDisplayName, setIdpDisplayName]   = useState('Microsoft');
  const [idpClientId, setIdpClientId]         = useState('');
  const [idpClientSecret, setIdpClientSecret] = useState('');
  const [idpAzureTenantId, setIdpAzureTenantId] = useState('');
  const [idpScopes, setIdpScopes]             = useState('openid email profile');
  const [idpAuthUrl, setIdpAuthUrl]           = useState('');
  const [idpTokenUrl, setIdpTokenUrl]         = useState('');
  const [idpJwksUrl, setIdpJwksUrl]           = useState('');
  const [idpIssuer, setIdpIssuer]             = useState('');

  // Platform admin (SysAdmin) condition — defaults to the Ontoris AD so the wizard is usable
  // out of the box, but stays a wizard field (never hardcoded) so a customer-run deployment
  // can point at its own AD. Persisted on the tenant doc as adminRole: { groupName, idpAlias }.
  const [adminGroupName, setAdminGroupName] = useState('ontoris-platform-admins');
  const [adminIdpAlias, setAdminIdpAlias]   = useState('microsoft');
  // null = not yet checked / dry-run endpoint unreachable — must not block Next.
  // false = blank group name or the server rejected the compiled condition — must block Next.
  const [adminRoleValid, setAdminRoleValid] = useState<boolean | null>(null);

  // Sourced from the Clusters collection via GET /api/clusters (clusterController.getAllClusters).
  const loadClusters = useCallback(() => {
    setClustersState('loading');
    setClustersError(null);
    getAllClusters()
      .then(r => {
        setClusters((r as any).data ?? []);
        setClustersState('ok');
      })
      .catch((e: any) => {
        // apiClient's interceptor rejects with { status, message, data } - a plain object,
        // not an Error with `.response` - so read `status` first and only then fall back.
        const status = e?.status ?? e?.response?.status;
        const detail = e?.data?.error || e?.message || 'request failed';
        setClusters([]);
        setClustersError(
          status === 401
            ? 'Not authorized (401) - the sign-in token expired or was not attached to the request. Reload the page to re-authenticate, then retry.'
            : `Could not load the cluster list${status ? ` (HTTP ${status})` : ''}: ${detail}`
        );
        setClustersState('error');
      });
  }, []);

  useEffect(() => { loadClusters(); }, [loadClusters]);

  useEffect(() => {
    getEnvironmentDefaults().then(r => setEnvDefaults((r as any).data ?? FALLBACK_ENV_DEFAULTS)).catch(() => {});
  }, []);

  // Recompute derived URL/JWKS fields whenever tenant name, env, local, or config defaults change.
  // tenantId is intentionally NOT set here — it stays empty until the user
  // types it explicitly, or gets a UUID at submission time.
  useEffect(() => {
    if (!tenantName.trim()) return;
    const { baseUrl, keycloakUrl, jwksBaseUrl } = computeUrls(tenantName, env, local, envDefaults);
    setForm(f => ({
      ...f,
      baseUrl,
      protocol: 'https',
      keycloakAdminUrl: keycloakUrl,
      keycloakPublicUrl: keycloakUrl,
      jwksHost: envDefaults.jwksLocal.host,
      jwksPort: envDefaults.jwksLocal.port,
      jwksBaseUrl,
    }));
  }, [tenantName, env, local, envDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute the secret store name default whenever local / config type / config defaults change.
  useEffect(() => {
    setForm(f => ({ ...f, secretStoreName: defaultSecretStoreName(local, configType, envDefaults) }));
  }, [local, configType, envDefaults]);

  // Pre-populate the Azure AD federation fields from the environment's platform-configured
  // App Registration (client secret excluded — never sent to the browser). Only fills fields
  // the user hasn't already typed something into.
  useEffect(() => {
    if (idpType !== 'azure-ad') return;
    const d = envDefaults.azureAdIdp[env];
    if (!idpClientId && d?.clientId) setIdpClientId(d.clientId);
    if (!idpAzureTenantId && d?.tenantId) setIdpAzureTenantId(d.tenantId);
  }, [idpType, env, envDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field: keyof ProvisionRequest, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  const { baseUrl, keycloakUrl, jwksBaseUrl } = tenantName.trim()
    ? computeUrls(tenantName, env, local, envDefaults)
    : { baseUrl: '', keycloakUrl: '', jwksBaseUrl: '' };

  const canNext = () => {
    if (step === 'identity') {
      return !!(tenantName.trim() && form.displayName && (!local || form.keycloakAdminPassword))
        && tenantIdCheck !== 'taken' && tenantIdCheck !== 'checking'
        && adminGroupName.trim() !== '' && adminRoleValid !== false;
    }
    if (step === 'platform') {
      return !!(form.databaseName && form.cluster && form.secretStoreName)
        && databaseNameCheck !== 'taken' && databaseNameCheck !== 'checking';
    }
    return true;
  };

  const runDatabaseNameCheck = (name: string) => {
    if (!name.trim()) { setDatabaseNameCheck('idle'); return; }
    setDatabaseNameCheck('checking');
    checkDatabaseNameAvailable(name.trim())
      .then(r => setDatabaseNameCheck(r.available ? 'available' : 'taken'))
      .catch(() => setDatabaseNameCheck('idle'));
  };

  const next = () => {
    const i = STEPS.indexOf(step);
    if (i >= STEPS.length - 1) return;
    if (step === 'identity' && !form.databaseName.trim()) {
      const dbName = defaultDatabaseName(tenantName, env, local);
      set('databaseName', dbName);
      runDatabaseNameCheck(dbName);
    }
    setStep(STEPS[i + 1]);
  };
  const prev = () => { const i = STEPS.indexOf(step); if (i > 0) setStep(STEPS[i - 1]); };

  const handleProvision = async () => {
    setLoading(true); setError(null);
    // Auto-generate realm ID with UUID if left empty
    const finalForm = form.tenantId.trim()
      ? form
      : { ...form, tenantId: crypto.randomUUID() };
    if (!form.tenantId.trim()) setForm(finalForm);
    // Build federatedAd config from IDP form state
    let federatedAd: any = null;
    if (idpType === 'azure-ad' && idpClientId) {
      federatedAd = {
        alias: idpAlias || 'microsoft',
        displayName: idpDisplayName || 'Microsoft',
        clientId: idpClientId,
        clientSecret: idpClientSecret, // may be blank — server fills it from the environment default
        azureTenantId: idpAzureTenantId,
        defaultScopes: idpScopes || 'openid email profile',
      };
    } else if (idpType === 'oidc' && idpClientId && idpClientSecret && idpAuthUrl && idpTokenUrl) {
      federatedAd = {
        alias: idpAlias,
        displayName: idpDisplayName,
        clientId: idpClientId,
        clientSecret: idpClientSecret,
        defaultScopes: idpScopes || 'openid email profile',
        authorizationUrl: idpAuthUrl,
        tokenUrl: idpTokenUrl,
        jwksUrl: idpJwksUrl || undefined,
        issuer: idpIssuer || undefined,
      };
    }

    const formWithIdp = {
      ...finalForm,
      environment: env,
      local,
      authMethods: {
        credential: true,
        otp: true,
        magicLink: true,
        federatedAd,
      },
      // Persisted on the tenant doc so the readiness probe can re-derive and re-verify the
      // condition. Server-owned shape (Phase 3, parallel dispatch): adminRole: { groupName, idpAlias }.
      adminRole: {
        groupName: adminGroupName.trim(),
        idpAlias: adminIdpAlias.trim() || 'microsoft',
      },
    };

    try {
      const resp = await provisionTenant(formWithIdp);
      // keycloakOutput is null when Keycloak provisioning failed - the Mongo document is still
      // saved and kcWarning explains it, so fall back to {} instead of throwing on field access
      // and losing the warning screen entirely.
      const kc   = (resp as any).keycloakOutput ?? {};
      const data = (resp as any).data;
      if ((resp as any).keycloakError) setKcWarning((resp as any).keycloakError);
      setResult({
        appClientSecret:        kc.appClientSecret,
        // The response emits the canonical `externalMagicLinkClientSecret`; `externalClientSecret`
        // is only an alias persisted on the tenant document and is never present here. Reading the
        // alias yielded undefined and crashed SecretRow on `.length`.
        externalClientSecret:   kc.externalMagicLinkClientSecret ?? kc.externalClientSecret,
        externalOtpClientSecret: kc.externalOtpClientSecret,
        emailSpiClientId:       kc.emailSpiClientId,
        emailSpiSecret:         kc.emailSpiSecret,
        redisUsername:          data.redis?.username ?? '',
        redisPasswordKey:       data.redis?.passwordSecretKey ?? '',
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message ?? 'Provisioning failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────

  if (result) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg">✓</div>
          <div>
            <h1 className="text-xl font-bold text-white">Tenant Provisioned</h1>
            <p className="text-gray-400 text-sm">
              <span className={`font-medium ${ENV_LABELS[env] === 'Production' ? 'text-green-400' : env === 'dev' ? 'text-blue-400' : 'text-purple-400'}`}>
                [{ENV_LABELS[env]}{local ? ' · Local' : ''}]
              </span>
              {' '}Realm <span className="font-mono text-blue-300">{form.tenantId}</span> → <span className="font-mono text-gray-300">https://{form.baseUrl}</span>
            </p>
          </div>
        </div>

        {kcWarning && (
          <div className="bg-orange-900/40 border border-orange-600 rounded p-3 mb-4 text-sm text-orange-300">
            <p className="font-semibold mb-0.5">Keycloak provisioning failed — MongoDB document saved.</p>
            <p className="text-xs opacity-80">{kcWarning}</p>
            <p className="text-xs mt-1 opacity-70">Run the Keycloak init script manually or retry provisioning after fixing the connection.</p>
          </div>
        )}
        {!kcWarning && (
          <div className="bg-yellow-900/40 border border-yellow-600 rounded p-3 mb-4 text-sm text-yellow-300">
            ⚠ Save these secrets now — they cannot be retrieved again.
          </div>
        )}

        <div className="space-y-1 mb-6">
          <SecretRow label="App client secret"           value={result.appClientSecret} />
          <SecretRow label="External magic-link secret"  value={result.externalClientSecret} />
          <SecretRow label="External OTP secret"         value={result.externalOtpClientSecret} />
          <SecretRow label="Email SPI client ID"         value={result.emailSpiClientId} />
          <SecretRow label="Email SPI secret"            value={result.emailSpiSecret} />
          <div className="flex items-center justify-between py-1 border-b border-gray-700 text-sm">
            <span className="text-gray-400 w-56 shrink-0">Redis username</span>
            <span className="font-mono text-gray-200">{result.redisUsername}</span>
          </div>
          <div className="flex items-center justify-between py-1 border-b border-gray-700 text-sm">
            <span className="text-gray-400 w-56 shrink-0">Redis password Vault key</span>
            <span className="font-mono text-gray-200">{result.redisPasswordKey}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={async () => {
              setKeysState('loading'); setKeysMsg('');
              try {
                const r: any = await initializeAll(form.tenantId);
                setKeysState('ok');
                setKeysMsg(`Keys initialized: ${r.initialized}/${r.total} services`);
              } catch (e: any) {
                setKeysState('error');
                setKeysMsg(e.message ?? 'Key initialization failed');
              }
            }}
            disabled={keysState === 'loading'}
            className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white rounded text-sm font-medium flex items-center gap-2"
          >
            {keysState === 'loading' && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            Initialize Keys
          </button>
          {keysMsg && (
            <span className={`self-center text-xs ${keysState === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{keysMsg}</span>
          )}
          <div className="ml-auto flex gap-3">
            <button onClick={() => navigate(`/key-management?tenantId=${form.tenantId}`)}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm">
              Manage Keys
            </button>
            <button onClick={() => navigate('/tenants')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm">
              Go to tenants
            </button>
            <button onClick={() => {
              setResult(null); setForm(emptyForm()); setTenantName(''); setKcWarning(null);
              setStep('identity'); setEnv('dev'); setLocal(false); setConfigType('azure');
              setDatabaseNameCheck('idle'); setKeysState('idle'); setKeysMsg('');
              setAdminGroupName('ontoris-platform-admins'); setAdminIdpAlias('microsoft'); setAdminRoleValid(null);
            }} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm">
              Provision another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Provision New Tenant</h1>
        <p className="text-gray-400 text-sm mt-1">Creates the Keycloak realm with all clients and the MongoDB tenant document.</p>
      </div>

      {/* Step tabs */}
      <div className="flex gap-1 mb-6">
        {STEPS.map(s => (
          <button key={s}
            onClick={() => STEPS.indexOf(s) < STEPS.indexOf(step) && setStep(s)}
            className={`flex-1 py-2 text-xs rounded transition-colors ${
              s === step ? 'bg-blue-600 text-white' :
              STEPS.indexOf(s) < STEPS.indexOf(step) ? 'bg-gray-600 text-gray-200 hover:bg-gray-500 cursor-pointer' :
              'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}>
            {STEP_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="bg-gray-800 rounded-lg p-6">

        {/* ── Step 1: Identity ─────────────────────────────────────────────── */}
        {step === 'identity' && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-5">Identity</h2>

            {/* Environment radio */}
            <Field label="Environment" required>
              <div className="flex gap-3 mt-1">
                {(['prod', 'dev', 'demo'] as Env[]).map(e => (
                  <button key={e} type="button" onClick={() => setEnv(e)}
                    className={`flex-1 py-2.5 rounded border-2 text-sm font-semibold transition-all ${env === e ? ENV_COLORS[e] : ENV_INACTIVE}`}>
                    {ENV_LABELS[e]}
                  </button>
                ))}
              </div>
            </Field>

            {/* Local / on-prem toggle */}
            <Field label="Deployment Target">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={local}
                  onChange={e => setLocal(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                />
                Local (on-prem / dev)
              </label>
              {local && (
                <p className="mt-1 text-xs text-yellow-400">
                  ⚠ Local mode only works with an on-premises deployment (Keycloak, Consul and Vault running locally) — not for cloud tenants.
                </p>
              )}
            </Field>

            {/* Display name — first */}
            <Field label="Display Name" hint="Full name shown in the UI (e.g. ACME Corp)" required>
              <Input
                value={form.displayName}
                onChange={e => set('displayName', e.target.value)}
                placeholder="ACME Corp"
              />
            </Field>

            {/* Tenant name (slug) — drives base URL */}
            <Field label="Tenant Name" hint="Short slug used to compute all URLs — lowercase, no spaces (e.g. acme)" required>
              <Input
                value={tenantName}
                onChange={e => {
                  const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
                  setTenantName(v);
                }}
                placeholder="acme"
              />
            </Field>

            {/* Tenant ID = Realm ID (they must be identical — the app relies on this) */}
            <Field label="Tenant ID / Realm ID" hint="Used as both the MongoDB tenantId and the Keycloak realm name — leave empty to auto-generate a UUID">
              <div className="relative">
                <Input
                  value={form.tenantId}
                  onChange={e => {
                    const v = e.target.value;
                    set('tenantId', v);
                    if (tenantIdDebounce.current) clearTimeout(tenantIdDebounce.current);
                    if (!v.trim()) { setTenantIdCheck('idle'); return; }
                    setTenantIdCheck('checking');
                    tenantIdDebounce.current = setTimeout(async () => {
                      try {
                        const r = await checkTenantIdAvailable(v.trim());
                        setTenantIdCheck(r.available ? 'available' : 'taken');
                      } catch { setTenantIdCheck('idle'); }
                    }, 500);
                  }}
                  placeholder="Auto-generated UUID if left empty"
                  className={`w-full px-3 py-2 rounded bg-gray-700 border text-gray-100 placeholder-gray-500 focus:outline-none text-sm pr-8 ${
                    tenantIdCheck === 'taken' ? 'border-red-500 focus:border-red-400' :
                    tenantIdCheck === 'available' ? 'border-green-500 focus:border-green-400' :
                    'border-gray-600 focus:border-blue-500'
                  }`}
                />
                {tenantIdCheck === 'checking' && (
                  <svg className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {tenantIdCheck === 'available' && (
                  <span className="absolute right-2 top-2 text-green-400 text-sm">✓</span>
                )}
                {tenantIdCheck === 'taken' && (
                  <span className="absolute right-2 top-2 text-red-400 text-sm">✗</span>
                )}
              </div>
              {tenantIdCheck === 'taken' && (
                <p className="mt-1 text-xs text-red-400">This Tenant ID is already in use — choose a different one.</p>
              )}
              {tenantIdCheck === 'available' && (
                <p className="mt-1 text-xs text-green-400">Available.</p>
              )}
            </Field>

            {/* Computed URLs — read-only display */}
            {tenantName.trim() && (
              <div className="rounded-lg bg-gray-900/60 border border-gray-700 px-4 py-3 mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Computed URLs</p>
                <UrlBadge label="App URL"   value={`https://${baseUrl}`} />
                <UrlBadge label="Keycloak"  value={keycloakUrl} />
                <UrlBadge label="JWKS"      value={jwksBaseUrl} />
              </div>
            )}

            <hr className="border-gray-700 my-4" />
            {local ? (
              <>
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Keycloak Admin Credentials</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Local realms have no pre-existing trust with the platform — provisioning authenticates directly against this Keycloak instance's own admin account.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Admin Username">
                    <Input value={form.keycloakAdminUser} onChange={e => set('keycloakAdminUser', e.target.value)} />
                  </Field>
                  <Field label="Admin Password" required>
                    <Input type="password" value={form.keycloakAdminPassword} onChange={e => set('keycloakAdminPassword', e.target.value)} placeholder="••••••••" />
                  </Field>
                </div>
              </>
            ) : (
              <div className="p-3 bg-gray-900/40 border border-gray-700 rounded text-xs text-gray-400">
                Using the platform's own service identity for cloud provisioning — no Keycloak admin credentials needed.
              </div>
            )}

            <hr className="border-gray-700 my-4" />
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Federated Identity Provider <span className="text-gray-500 font-normal">(optional)</span></h3>

            <Field label="Provider Type">
              <div className="flex gap-2">
                {(['none', 'azure-ad', 'oidc'] as IdpType[]).map(t => (
                  <button key={t} type="button"
                    onClick={() => {
                      setIdpType(t);
                      if (t === 'azure-ad') { setIdpAlias('microsoft'); setIdpDisplayName('Microsoft'); }
                    }}
                    className={`px-3 py-1.5 rounded border text-sm transition-all ${idpType === t ? 'border-blue-500 bg-blue-600/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500'}`}>
                    {t === 'none' ? 'None' : t === 'azure-ad' ? 'Azure AD' : 'Generic OIDC'}
                  </button>
                ))}
              </div>
            </Field>

            {idpType !== 'none' && (
              <div className="space-y-0 border border-gray-700 rounded-lg p-4 bg-gray-900/40">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="IDP Alias" hint="Keycloak alias (e.g. microsoft)">
                    <Input value={idpAlias} onChange={e => setIdpAlias(e.target.value)} placeholder="microsoft" />
                  </Field>
                  <Field label="Display Name">
                    <Input value={idpDisplayName} onChange={e => setIdpDisplayName(e.target.value)} placeholder="Microsoft" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Client ID" required>
                    <Input value={idpClientId} onChange={e => setIdpClientId(e.target.value)} placeholder="Azure App Registration client ID" />
                  </Field>
                  <Field label="Client Secret" hint={idpType === 'azure-ad' ? `Leave blank to use the platform-configured secret for ${ENV_LABELS[env]}` : undefined}>
                    <Input type="password" value={idpClientSecret} onChange={e => setIdpClientSecret(e.target.value)} placeholder="••••••••" />
                  </Field>
                </div>

                {idpType === 'azure-ad' && (
                  <Field label="Azure Tenant ID" hint="Azure Directory (tenant) ID from your App Registration" required>
                    <Input value={idpAzureTenantId} onChange={e => setIdpAzureTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Field>
                )}

                {idpType === 'oidc' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Authorization URL" required>
                        <Input value={idpAuthUrl} onChange={e => setIdpAuthUrl(e.target.value)} placeholder="https://idp.example.com/oauth2/authorize" />
                      </Field>
                      <Field label="Token URL" required>
                        <Input value={idpTokenUrl} onChange={e => setIdpTokenUrl(e.target.value)} placeholder="https://idp.example.com/oauth2/token" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="JWKS URL">
                        <Input value={idpJwksUrl} onChange={e => setIdpJwksUrl(e.target.value)} placeholder="https://idp.example.com/.well-known/jwks.json" />
                      </Field>
                      <Field label="Issuer">
                        <Input value={idpIssuer} onChange={e => setIdpIssuer(e.target.value)} placeholder="https://idp.example.com" />
                      </Field>
                    </div>
                  </>
                )}

                <Field label="Scopes">
                  <Input value={idpScopes} onChange={e => setIdpScopes(e.target.value)} placeholder="openid email profile" />
                </Field>

                {idpType === 'azure-ad' && idpAzureTenantId && (
                  <div className="mt-2 p-2 bg-blue-900/20 border border-blue-800/50 rounded text-xs text-blue-400">
                    Azure AD redirect URI to register: <span className="font-mono">{form.keycloakAdminUrl}/realms/{form.tenantId || '(realm-id)'}/broker/{idpAlias}/endpoint</span>
                  </div>
                )}
              </div>
            )}

            <hr className="border-gray-700 my-4" />
            <AdminRoleConditionEditor
              tenantId={form.tenantId}
              groupName={adminGroupName}
              onGroupNameChange={setAdminGroupName}
              idpAlias={adminIdpAlias}
              onIdpAliasChange={setAdminIdpAlias}
              onValidityChange={setAdminRoleValid}
            />
          </div>
        )}

        {/* ── Step 2: Infrastructure ────────────────────────────────────────── */}
        {step === 'platform' && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Infrastructure</h2>

            <Field label="Database Name" hint="MongoDB database for this tenant's data — must be unique" required>
              <div className="relative">
                <Input
                  value={form.databaseName}
                  onChange={e => {
                    const v = e.target.value;
                    set('databaseName', v);
                    if (databaseNameDebounce.current) clearTimeout(databaseNameDebounce.current);
                    if (!v.trim()) { setDatabaseNameCheck('idle'); return; }
                    setDatabaseNameCheck('checking');
                    databaseNameDebounce.current = setTimeout(() => runDatabaseNameCheck(v), 500);
                  }}
                  placeholder="AcmeDatastore"
                  className={`w-full px-3 py-2 rounded bg-gray-700 border text-gray-100 placeholder-gray-500 focus:outline-none text-sm pr-8 ${
                    databaseNameCheck === 'taken' ? 'border-red-500 focus:border-red-400' :
                    databaseNameCheck === 'available' ? 'border-green-500 focus:border-green-400' :
                    'border-gray-600 focus:border-blue-500'
                  }`}
                />
                {databaseNameCheck === 'checking' && (
                  <svg className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {databaseNameCheck === 'available' && (
                  <span className="absolute right-2 top-2 text-green-400 text-sm">✓</span>
                )}
                {databaseNameCheck === 'taken' && (
                  <span className="absolute right-2 top-2 text-red-400 text-sm">✗</span>
                )}
              </div>
              {databaseNameCheck === 'taken' && (
                <p className="mt-1 text-xs text-red-400">This database name is already in use — choose a different one.</p>
              )}
              {databaseNameCheck === 'available' && (
                <p className="mt-1 text-xs text-green-400">Available.</p>
              )}
            </Field>

            <Field label="Cluster" hint="MongoDB cluster this tenant belongs to — read from the Clusters collection" required>
              <Select
                value={form.cluster}
                onChange={e => set('cluster', e.target.value)}
                disabled={clustersState !== 'ok' || clusters.length === 0}
              >
                <option value="">
                  {clustersState === 'loading'
                    ? 'Loading clusters…'
                    : clustersState === 'error'
                      ? '— Cluster list unavailable —'
                      : clusters.length === 0
                        ? '— No clusters defined —'
                        : '— Select a cluster —'}
                </option>
                {clusters.map(c => (
                  <option key={c._id} value={c.clusterId}>{c.name ? `${c.name} (${c.clusterId})` : c.clusterId}</option>
                ))}
              </Select>
              {clustersState === 'error' && (
                <div className="mt-2 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-300">
                  <p>{clustersError}</p>
                  <button type="button" onClick={loadClusters} className="mt-1 underline hover:text-red-200">
                    Retry
                  </button>
                </div>
              )}
              {clustersState === 'ok' && clusters.length === 0 && (
                <p className="mt-2 text-xs text-yellow-400">
                  The request succeeded but the Clusters collection returned no entries — add a cluster
                  under Db Clusters before provisioning a tenant.
                </p>
              )}
            </Field>

            {local && (
              <Field label="Local Config Type" hint="How secrets are stored for this on-prem deployment">
                <div className="flex gap-2">
                  {(['azure', 'local'] as ConfigType[]).map(t => (
                    <button key={t} type="button" onClick={() => setConfigType(t)}
                      className={`px-3 py-1.5 rounded border text-sm transition-all ${configType === t ? 'border-blue-500 bg-blue-600/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500'}`}>
                      {t === 'azure' ? 'Azure (standard)' : 'Local (Consul + Vault)'}
                    </button>
                  ))}
                </div>
              </Field>
            )}

            {local && configType === 'local' && (
              <div className="rounded-lg bg-gray-900/60 border border-gray-700 px-4 py-3 mb-4 text-xs text-gray-400">
                <p className="font-semibold text-gray-300 mb-1">Consul / Vault addresses</p>
                <p>Consul: <span className="font-mono text-blue-300">{envDefaults.localInfra.consulAddr}</span></p>
                <p>Vault: <span className="font-mono text-blue-300">{envDefaults.localInfra.vaultAddr}</span></p>
              </div>
            )}

            <Field label="Secret Store Name" hint="Azure Key Vault (or Consul/Vault secret engine) name for this tenant's secrets" required>
              <Input value={form.secretStoreName} onChange={e => set('secretStoreName', e.target.value)} placeholder="ontoris-nonprod-akv" />
            </Field>

            <hr className="border-gray-700 my-4" />
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Email (optional)</h3>

            <Field label="Notification Address">
              <Input value={form.emailNotificationAddress} onChange={e => set('emailNotificationAddress', e.target.value)} placeholder={`DoNotReply@notification.${baseUrl || 'acme.ontoris.app'}`} />
            </Field>

            <Field label="Notification Display Name">
              <Input value={form.emailNotificationDisplayName} onChange={e => set('emailNotificationDisplayName', e.target.value)} placeholder={`${form.displayName || 'Acme'} Notifications`} />
            </Field>

            <Field label="Azure Communication Services Endpoint" hint="Leave empty to configure later">
              <Input value={form.acsEndpoint} onChange={e => set('acsEndpoint', e.target.value)} placeholder="https://..." />
            </Field>
          </div>
        )}

        {/* ── Step 3: Review ────────────────────────────────────────────────── */}
        {step === 'review' && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Review &amp; Provision</h2>

            {/* Environment badge */}
            <div className="flex items-center gap-2 mb-5">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${ENV_COLORS[env]}`}>
                <span className="w-2 h-2 rounded-full bg-current opacity-80" />
                {ENV_LABELS[env]} environment
              </div>
              {local && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500 bg-yellow-600/20 text-yellow-300 text-sm font-semibold">
                  Local / on-prem
                </div>
              )}
            </div>

            <div className="space-y-2.5 text-sm mb-5">
              {[
                ['Tenant ID = Realm ID', form.tenantId || '(UUID will be generated)'],
                ['Display Name',      form.displayName],
                ['App URL',           `https://${form.baseUrl}`],
                ['Keycloak',          form.keycloakAdminUrl],
                ['JWKS Provider',     form.jwksBaseUrl],
                ['Database',          form.databaseName],
                ['Cluster',           form.cluster],
                ['Secret Store',      form.secretStoreName],
                ...(idpType !== 'none' ? [['Identity Provider', `${idpDisplayName} (${idpAlias})`]] : []),
                ['SysAdmin group',    `${adminGroupName} (via ${adminIdpAlias})`],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="text-gray-400 shrink-0 w-36">{label}</span>
                  <span className="text-gray-100 font-mono text-right break-all">{value}</span>
                </div>
              ))}
            </div>

            <div className="p-3 bg-blue-900/30 border border-blue-700 rounded text-sm text-blue-300">
              <p className="font-semibold mb-1">What will be created:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-200">
                <li>Keycloak realm <code className="bg-blue-900/50 px-1 rounded">{form.tenantId}</code> on <code className="bg-blue-900/50 px-1 rounded">{form.keycloakAdminUrl}</code></li>
                <li>Client scope <code className="bg-blue-900/50 px-1 rounded">ontoris-platform</code> + mappers</li>
                <li>App client <code className="bg-blue-900/50 px-1 rounded">ontoris-client</code> (PKCE + secret)</li>
                <li>14 service clients (JWKS / ES256)</li>
                <li>External clients: ontoris-external, ontoris-external-otp</li>
                <li>Auth flows: email-otp-browser, magic-link-browser</li>
                <li>Groups: internal-users, external-users</li>
                <li>MongoDB tenant document (status: disabled)</li>
                <li>SysAdmin role condition — group <code className="bg-blue-900/50 px-1 rounded">{adminGroupName}</code> via IdP <code className="bg-blue-900/50 px-1 rounded">{adminIdpAlias}</code></li>
                {idpType !== 'none' && <li>Federated IDP <code className="bg-blue-900/50 px-1 rounded">{idpAlias}</code> ({idpType === 'azure-ad' ? 'Azure AD via generic OIDC' : 'Generic OIDC'}) + adRoles mapper</li>}
                {local && <li>Keycloak authenticated via the realm's own admin account (admin-cli grant)</li>}
              </ul>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-900/40 border border-red-700 rounded text-sm text-red-300">{error}</div>
            )}
          </div>
        )}

        {/* ── Navigation ───────────────────────────────────────────────────── */}
        <div className="flex justify-between mt-6">
          <button
            onClick={step === 'identity' ? () => navigate('/tenants') : prev}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm"
            disabled={loading}
          >
            {step === 'identity' ? 'Cancel' : '← Back'}
          </button>

          {step !== 'review' ? (
            <button onClick={next} disabled={!canNext()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded text-sm font-medium">
              Next →
            </button>
          ) : (
            <button onClick={handleProvision} disabled={loading || tenantIdCheck === 'taken' || tenantIdCheck === 'checking' || databaseNameCheck === 'taken' || databaseNameCheck === 'checking'}
              className="px-6 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white rounded text-sm font-medium flex items-center gap-2">
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {loading ? 'Provisioning…' : '🚀 Provision Tenant'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantProvisionPage;
